export type AffiliateBrand =
  | 'ola-electric' | 'ather' | 'tvs' | 'bajaj' | 'hero'
  | 'royal-enfield' | 'honda' | 'yamaha' | 'suzuki' | 'kawasaki'
  | 'tata' | 'maruti' | 'hyundai' | 'mahindra' | 'renault'
  | 'bmw' | 'ktm' | 'triumph' | 'ducati' | 'harley-davidson'
  | 'kia' | 'toyota' | 'bsa' | 'mg'
  | 'mercedes-benz' | 'nissan' | 'volkswagen' | 'yezdi'
  | 'byd' | 'audi';

const AFFILIATE_BASE_URLS: Record<AffiliateBrand, string> = {
  // EV two-wheelers — ordered by Feb 2026 sales volume
  'tvs': 'https://www.tvsmotor.com',
  'bajaj': 'https://www.bajajauto.com',
  'ather': 'https://atherenergy.com',
  'hero': 'https://www.heromotocorp.com',
  'ola-electric': 'https://olaelectric.com',
  // Indian car OEMs
  'tata': 'https://cars.tatamotors.com',
  'maruti': 'https://www.nexaexperience.com',
  'hyundai': 'https://www.hyundai.com/in',
  'mahindra': 'https://www.mahindra.com/auto',
  'renault': 'https://www.renault.co.in',
  'kia': 'https://www.kia.com/in',
  'toyota': 'https://www.toyotabharat.com',
  'mg': 'https://www.mgmotor.co.in',
  'mercedes-benz': 'https://www.mercedes-benz.co.in',
  'nissan': 'https://www.nissan.in',
  'volkswagen': 'https://www.volkswagen.co.in',
  'byd': 'https://www.byd.com/in',
  'audi': 'https://www.audi.in',
  // Indian motorcycle brands
  'yezdi': 'https://www.yezdi.com',
  // International motorcycle brands
  'royal-enfield': 'https://www.royalenfield.com/in',
  'honda': 'https://www.honda2wheelersindia.com',
  'yamaha': 'https://www.yamaha-motor-india.com',
  'suzuki': 'https://www.suzukimotorcycle.co.in',
  'kawasaki': 'https://www.kawasaki-india.com',
  'ktm': 'https://www.ktm.com/en-in',
  'bmw': 'https://www.bmw-motorrad.in',
  'triumph': 'https://www.triumphmotorcycles.in',
  'ducati': 'https://www.ducati.com/in',
  'harley-davidson': 'https://www.harley-davidson.com/in',
  'bsa': 'https://www.bsamotorcycles.com',
};

// Normalize brand strings from post data to AffiliateBrand keys
const BRAND_ALIASES: Record<string, AffiliateBrand> = {
  'royal enfield': 'royal-enfield',
  're': 'royal-enfield',
  'ola': 'ola-electric',
  'ola electric': 'ola-electric',
  'harley': 'harley-davidson',
  'harley davidson': 'harley-davidson',
  'maruti suzuki': 'maruti',
  'tata motors': 'tata',
  'bmw motorrad': 'bmw',
  'mercedes': 'mercedes-benz',
  'mercedes benz': 'mercedes-benz',
  'vw': 'volkswagen',
};

export function resolveAffiliateBrand(brand: string): AffiliateBrand | null {
  const normalized = brand.toLowerCase().trim();
  if (normalized in AFFILIATE_BASE_URLS) return normalized as AffiliateBrand;
  if (normalized in BRAND_ALIASES) return BRAND_ALIASES[normalized];
  return null;
}

export function buildAffiliateUrl(
  brand: AffiliateBrand,
  source: string = 'feed',
): string {
  const base = AFFILIATE_BASE_URLS[brand];
  return `${base}?utm_source=motowalla&utm_medium=${source}&utm_campaign=check-price`;
}

export function buildTestDriveUrl(
  brand: AffiliateBrand,
  slug: string,
): string {
  const base = AFFILIATE_BASE_URLS[brand];
  return `${base}?utm_source=motowalla&utm_medium=spec-page&utm_campaign=test-drive&utm_content=${encodeURIComponent(slug)}`;
}

// UI-073: Build affiliate URL for vehicle launch article CTAs
export function buildLaunchArticleUrl(
  brand: AffiliateBrand,
  articleSlug: string,
): string {
  const base = AFFILIATE_BASE_URLS[brand];
  return `${base}?utm_source=motowalla&utm_medium=launch-article&utm_campaign=book-test-drive&utm_content=${encodeURIComponent(articleSlug)}`;
}

export type InsuranceType = 'motorcycles' | 'ev' | 'cars' | 'scooters' | 'adv';

export const INSURANCE_URLS: Record<InsuranceType, string> = {
  motorcycles: 'https://www.policybazaar.com/motor-insurance/two-wheeler-insurance/?utm_source=motowalla&utm_medium=postcard&utm_campaign=insurance',
  adv: 'https://www.policybazaar.com/motor-insurance/two-wheeler-insurance/?utm_source=motowalla&utm_medium=postcard&utm_campaign=insurance',
  ev: 'https://www.insurancedekho.com/two-wheeler-insurance?utm_source=motowalla&utm_medium=postcard&utm_campaign=insurance',
  cars: 'https://www.policybazaar.com/motor-insurance/car-insurance/?utm_source=motowalla&utm_medium=postcard&utm_campaign=insurance',
  scooters: 'https://www.insurancedekho.com/two-wheeler-insurance?utm_source=motowalla&utm_medium=postcard&utm_campaign=insurance',
};

// MONET-001: Insurance affiliate partners for contextual CTAs
export interface InsurancePartner {
  name: string;
  slug: string;
  url: (category: InsuranceType) => string;
}

export const INSURANCE_PARTNERS: InsurancePartner[] = [
  {
    name: 'HDFC Ergo',
    slug: 'hdfc-ergo',
    url: (cat) =>
      cat === 'cars'
        ? 'https://www.hdfcergo.com/motor-insurance/car-insurance?utm_source=motowalla&utm_medium=article-cta&utm_campaign=insurance'
        : 'https://www.hdfcergo.com/motor-insurance/two-wheeler-insurance?utm_source=motowalla&utm_medium=article-cta&utm_campaign=insurance',
  },
  {
    name: 'Acko',
    slug: 'acko',
    url: (cat) =>
      cat === 'cars'
        ? 'https://www.acko.com/car-insurance/?utm_source=motowalla&utm_medium=article-cta&utm_campaign=insurance'
        : 'https://www.acko.com/two-wheeler-insurance/?utm_source=motowalla&utm_medium=article-cta&utm_campaign=insurance',
  },
  {
    name: 'Tata AIG',
    slug: 'tata-aig',
    url: (cat) =>
      cat === 'cars'
        ? 'https://www.tataaig.com/motor-insurance/car-insurance?utm_source=motowalla&utm_medium=article-cta&utm_campaign=insurance'
        : 'https://www.tataaig.com/motor-insurance/two-wheeler-insurance?utm_source=motowalla&utm_medium=article-cta&utm_campaign=insurance',
  },
  {
    name: 'PolicyBazaar',
    slug: 'policybazaar',
    url: (cat) =>
      cat === 'cars'
        ? 'https://www.policybazaar.com/motor-insurance/car-insurance/?utm_source=motowalla&utm_medium=article-inline&utm_campaign=insurance'
        : 'https://www.policybazaar.com/motor-insurance/two-wheeler-insurance/?utm_source=motowalla&utm_medium=article-inline&utm_campaign=insurance',
  },
  {
    name: 'Digit Insurance',
    slug: 'digit',
    url: (cat) =>
      cat === 'cars'
        ? 'https://www.godigit.com/motor-insurance/car-insurance?utm_source=motowalla&utm_medium=article-inline&utm_campaign=insurance'
        : 'https://www.godigit.com/motor-insurance/two-wheeler-insurance?utm_source=motowalla&utm_medium=article-inline&utm_campaign=insurance',
  },
];

export type EMIType = 'motorcycles' | 'ev' | 'scooters' | 'cars';

export const EMI_URLS: Record<EMIType, string> = {
  motorcycles: 'https://www.bankbazaar.com/two-wheeler-loan.html?utm_source=motowalla&utm_medium=cta&utm_campaign=emi-calculator',
  ev: 'https://www.bankbazaar.com/two-wheeler-loan.html?utm_source=motowalla&utm_medium=cta&utm_campaign=emi-calculator',
  scooters: 'https://www.bankbazaar.com/two-wheeler-loan.html?utm_source=motowalla&utm_medium=cta&utm_campaign=emi-calculator',
  cars: 'https://www.bankbazaar.com/car-loan.html?utm_source=motowalla&utm_medium=cta&utm_campaign=emi-calculator',
};

// MONET-002: Car loan EMI affiliate partners for contextual CTAs
export type CarLoanCategory = 'cars' | 'ev';

export interface CarLoanPartner {
  name: string;
  slug: string;
  url: (category: CarLoanCategory) => string;
}

// MONET-004: "Get Best Price from Dealer" lead-gen partners
export type DealerPriceCategory = 'cars' | 'ev' | 'motorcycles' | 'scooters';

export interface DealerPricePartner {
  name: string;
  slug: string;
  categories: DealerPriceCategory[];
  url: (category: DealerPriceCategory) => string;
}

export const DEALER_PRICE_PARTNERS: DealerPricePartner[] = [
  {
    name: 'CarDekho',
    slug: 'cardekho',
    categories: ['cars', 'ev'],
    url: (cat) =>
      cat === 'ev'
        ? 'https://www.cardekho.com/electric-cars?utm_source=motowalla&utm_medium=article-cta&utm_campaign=best-price'
        : 'https://www.cardekho.com/best-car-deals?utm_source=motowalla&utm_medium=article-cta&utm_campaign=best-price',
  },
  {
    name: 'CarWale',
    slug: 'carwale',
    categories: ['cars', 'ev'],
    url: () =>
      'https://www.carwale.com/best-car-deals/?utm_source=motowalla&utm_medium=article-cta&utm_campaign=best-price',
  },
  {
    name: 'BikeWale',
    slug: 'bikewale',
    categories: ['motorcycles', 'scooters'],
    url: () =>
      'https://www.bikewale.com/best-bike-deals/?utm_source=motowalla&utm_medium=article-cta&utm_campaign=best-price',
  },
  {
    name: 'ZigWheels',
    slug: 'zigwheels',
    categories: ['cars', 'ev', 'motorcycles', 'scooters'],
    url: (cat) =>
      cat === 'motorcycles' || cat === 'scooters'
        ? 'https://www.zigwheels.com/best-bike-offers?utm_source=motowalla&utm_medium=article-cta&utm_campaign=best-price'
        : 'https://www.zigwheels.com/best-car-offers?utm_source=motowalla&utm_medium=article-cta&utm_campaign=best-price',
  },
];

export const CAR_LOAN_PARTNERS: CarLoanPartner[] = [
  {
    name: 'BankBazaar',
    slug: 'bankbazaar',
    url: (cat) =>
      cat === 'ev'
        ? 'https://www.bankbazaar.com/car-loan/electric-car-loan.html?utm_source=motowalla&utm_medium=article-cta&utm_campaign=car-loan'
        : 'https://www.bankbazaar.com/car-loan.html?utm_source=motowalla&utm_medium=article-cta&utm_campaign=car-loan',
  },
  {
    name: 'PaisaBazaar',
    slug: 'paisabazaar',
    url: (cat) =>
      cat === 'ev'
        ? 'https://www.paisabazaar.com/car-loan/electric-car-loan/?utm_source=motowalla&utm_medium=article-cta&utm_campaign=car-loan'
        : 'https://www.paisabazaar.com/car-loan/?utm_source=motowalla&utm_medium=article-cta&utm_campaign=car-loan',
  },
  {
    name: 'Bajaj Finance',
    slug: 'bajaj-finance',
    url: () =>
      'https://www.bajajfinserv.in/car-loan?utm_source=motowalla&utm_medium=article-cta&utm_campaign=car-loan',
  },
];

// MON-010: Bank EMI affiliate partners — shown on spec page EMI widget
export type EMILoanCategory = 'cars' | 'motorcycles' | 'scooters' | 'ev';

export interface BankEMIPartner {
  name: string;
  slug: string;
  rate: string;
  categories: EMILoanCategory[];
  url: (category: EMILoanCategory) => string;
}

export const BANK_EMI_PARTNERS: BankEMIPartner[] = [
  {
    name: 'HDFC Auto Loan',
    slug: 'hdfc-auto',
    rate: '8.50%',
    categories: ['cars', 'ev', 'motorcycles', 'scooters'],
    url: (cat) =>
      cat === 'cars' || cat === 'ev'
        ? 'https://www.hdfcbank.com/personal/borrow/popular-loans/car-loan?utm_source=motowalla&utm_medium=emi-widget&utm_campaign=bank-emi'
        : 'https://www.hdfcbank.com/personal/borrow/popular-loans/two-wheeler-loan?utm_source=motowalla&utm_medium=emi-widget&utm_campaign=bank-emi',
  },
  {
    name: 'SBI Car Loan',
    slug: 'sbi-car',
    rate: '8.65%',
    categories: ['cars', 'ev', 'motorcycles', 'scooters'],
    url: (cat) =>
      cat === 'cars' || cat === 'ev'
        ? 'https://sbi.co.in/web/personal-banking/loans/auto-loans/car-loan?utm_source=motowalla&utm_medium=emi-widget&utm_campaign=bank-emi'
        : 'https://sbi.co.in/web/personal-banking/loans/auto-loans/two-wheeler-loan?utm_source=motowalla&utm_medium=emi-widget&utm_campaign=bank-emi',
  },
  {
    name: 'ICICI Bank',
    slug: 'icici-bank',
    rate: '8.75%',
    categories: ['cars', 'ev', 'motorcycles', 'scooters'],
    url: (cat) =>
      cat === 'cars' || cat === 'ev'
        ? 'https://www.icicibank.com/personal-banking/loans/car-loan?utm_source=motowalla&utm_medium=emi-widget&utm_campaign=bank-emi'
        : 'https://www.icicibank.com/personal-banking/loans/two-wheeler-loan?utm_source=motowalla&utm_medium=emi-widget&utm_campaign=bank-emi',
  },
];

// MON-020: EV Loan affiliate partners — shown on subsidy countdown banner
export type EVLoanCategory = 'ev-2w' | 'ev-car';

export interface EVLoanPartner {
  name: string;
  slug: string;
  rate: string;
  url: (category: EVLoanCategory) => string;
}

export const EV_LOAN_PARTNERS: EVLoanPartner[] = [
  {
    name: 'HDFC EV Loan',
    slug: 'hdfc-ev',
    rate: '8.50%',
    url: (cat) =>
      cat === 'ev-car'
        ? 'https://www.hdfcbank.com/personal/borrow/popular-loans/car-loan?utm_source=motowalla&utm_medium=subsidy-banner&utm_campaign=ev-loan'
        : 'https://www.hdfcbank.com/personal/borrow/popular-loans/two-wheeler-loan?utm_source=motowalla&utm_medium=subsidy-banner&utm_campaign=ev-loan',
  },
  {
    name: 'ICICI EV Loan',
    slug: 'icici-ev',
    rate: '8.75%',
    url: (cat) =>
      cat === 'ev-car'
        ? 'https://www.icicibank.com/personal-banking/loans/car-loan?utm_source=motowalla&utm_medium=subsidy-banner&utm_campaign=ev-loan'
        : 'https://www.icicibank.com/personal-banking/loans/two-wheeler-loan?utm_source=motowalla&utm_medium=subsidy-banner&utm_campaign=ev-loan',
  },
  {
    name: 'Axis EV Loan',
    slug: 'axis-ev',
    rate: '9.00%',
    url: (cat) =>
      cat === 'ev-car'
        ? 'https://www.axisbank.com/retail/loans/car-loan?utm_source=motowalla&utm_medium=subsidy-banner&utm_campaign=ev-loan'
        : 'https://www.axisbank.com/retail/loans/two-wheeler-loan?utm_source=motowalla&utm_medium=subsidy-banner&utm_campaign=ev-loan',
  },
];

// MONET-006: Spinny Used-Car Affiliate CTA — Buy/Sell Pre-Owned
export type UsedCarCategory = 'cars' | 'ev';

export interface UsedCarPartner {
  name: string;
  slug: string;
  tagline: string;
  buyUrl: (category: UsedCarCategory) => string;
  sellUrl: string;
}

export const USED_CAR_PARTNERS: UsedCarPartner[] = [
  {
    name: 'Spinny',
    slug: 'spinny',
    tagline: 'Full circle trust',
    buyUrl: (cat) =>
      cat === 'ev'
        ? 'https://www.spinny.com/used-electric-cars/?utm_source=motowalla&utm_medium=article-cta&utm_campaign=used-car-buy'
        : 'https://www.spinny.com/used-cars/?utm_source=motowalla&utm_medium=article-cta&utm_campaign=used-car-buy',
    sellUrl: 'https://www.spinny.com/sell-used-car/?utm_source=motowalla&utm_medium=article-cta&utm_campaign=used-car-sell',
  },
  {
    name: 'Cars24',
    slug: 'cars24',
    tagline: 'India\'s best way to buy & sell cars',
    buyUrl: (cat) =>
      cat === 'ev'
        ? 'https://www.cars24.com/buy-used-electric-cars/?utm_source=motowalla&utm_medium=article-cta&utm_campaign=used-car-buy'
        : 'https://www.cars24.com/buy-used-cars/?utm_source=motowalla&utm_medium=article-cta&utm_campaign=used-car-buy',
    sellUrl: 'https://www.cars24.com/sell-car/?utm_source=motowalla&utm_medium=article-cta&utm_campaign=used-car-sell',
  },
];
